import { Injectable, inject } from '@angular/core';
import { Observable, of, catchError, map } from 'rxjs';
import { ApiService } from './api.service';
import { LocalStorageService } from './local-storage.service';
import { SignalsStoreService } from './signals-store.service';
import { LOCALSTORAGE_KEYS } from './constants/databases';
import { Session } from './types/session.type';
import { ApiResponse } from './common/types';
import { RequestHandlerParams } from './types/api-service.types';
import { InvoiceHistoryResponse } from '../settings/billing/invoice-history/invoice-history.types';

/**
 * Keeps the past-due warning (DEV-10295) from outliving the balance that caused
 * it.
 *
 * `billing.isAtRisk` only ever arrives in the full session payload written by
 * login, and every later write patches around it, so the flag is a snapshot
 * frozen at login. Customers who settled their balance kept the banner and the
 * load-time modal for as long as their session lasted — weeks, with the extended
 * session — and the CTA itself could not clear it (DEV-10988).
 */
@Injectable({ providedIn: 'root' })
export class PastDueService {
  private apiService = inject(ApiService);
  private localStorageService = inject(LocalStorageService);
  private signalsStoreService = inject(SignalsStoreService);

  private endpoint = '/account/invoice-history';

  private session(): Session | null {
    return this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
  }

  /**
   * Re-checks the login-time flag against the invoices the customer actually
   * owes, which is the one live source the storefront already has: `pastDueOnly`
   * returns the same set the banner and the popup were built around, so an empty
   * page means there is nothing left to warn about.
   *
   * Emits whether the account is still at risk. Callers that are not flagged get
   * a synchronous `false` and never pay for the round trip. Anything short of a
   * clean empty response leaves the warning up: showing it one session too long
   * is the cheaper mistake, since the whole point of the feature is that a
   * suspension should never be the first thing a customer hears about.
   */
  revalidate(): Observable<boolean> {
    const session = this.session();
    const billing = session?.billing;

    if (!billing?.isAtRisk) return of(false);
    // Suspended accounts have their own screen; the reminder is noise there.
    if (session?.settings?.isSuspended) return of(false);

    // One invoice is enough to answer the question, so the page stays small.
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoint}?page=0&len=1&pastDueOnly=true`,
      method: 'GET',
      apiV3: true,
      showErrorMessage: false,
    };

    return this.apiService.handleRequest<ApiResponse<InvoiceHistoryResponse>>(params).pipe(
      map(response => {
        const stillOwes = (response?.data?.length ?? 1) > 0;
        if (!stillOwes) this.clear();
        return stillOwes;
      }),
      catchError(() => of(true)),
    );
  }

  /**
   * Drops the flag everything past-due is gated on, so the banner repaints out
   * of the way in the same tick. If the account really is still behind, the next
   * login brings the flag back with the session payload.
   */
  clear(): void {
    const session = this.session();
    if (!session?.billing?.isAtRisk) return;

    this.signalsStoreService.updateSession({
      billing: { ...session.billing, isAtRisk: false },
    });
  }
}
